'use client'

import { useMemo } from 'react'
import {
  resolveLinkedPeople,
  useDocumentView,
  useDocumentStatus,
} from '@/document'
import type { Entry } from '@/document'

// Entry with people and topics linked
export interface EntryWithRelations extends Entry {
  people: Array<{ id: string; name: string }>
  topics: Array<{ id: string; name: string }>
}

export interface LibraryData {
  movies: EntryWithRelations[]
  books: EntryWithRelations[]
  artists: EntryWithRelations[]
  labels: EntryWithRelations[]
}

export const libraryQueryKey = ['library'] as const

export function useLibraryData() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const data = useMemo(() => {
    if (!doc) return null

    // Get all entries excluding archived ones
    const allEntries = Object.values(doc.entries).filter(
      (entry) => !entry.archivedAt,
    )

    // Helper to attach people and topics to entries
    const withRelations = (entry: Entry): EntryWithRelations => {
      const linkedPeople = resolveLinkedPeople(entry.id, doc)
      // Map people entries to {id, name} format expected by components
      const people = linkedPeople.map((p) => ({
        id: p.id,
        name: p.title, // Person name is stored in title field
      }))

      // Resolve topics from topicIds
      const topics = (entry.topicIds || [])
        .map((topicId) => doc.topics[topicId])
        .filter((topic) => topic) // Filter out undefined topics
        .map((topic) => ({
          id: topic.id,
          name: topic.name,
        }))

      return {
        ...entry,
        people,
        topics,
      }
    }

    // Separate by type and return entries with relations
    const movies = allEntries
      .filter((e) => e.type === 'movie')
      .map(withRelations)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

    const books = allEntries
      .filter((e) => e.type === 'book')
      .map(withRelations)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

    const artists = allEntries
      .filter((e) => e.type === 'artist')
      .map(withRelations)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

    const labels = allEntries
      .filter((e) => e.type === 'label')
      .map(withRelations)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

    return {
      movies,
      books,
      artists,
      labels,
    }
  }, [doc])

  return {
    data,
    isLoading: !ready,
    error: null,
    isSuccess: ready,
    isError: false,
  }
}

/**
 * Prefetch library data.
 * Note: With client-side IndexedDB, prefetching is less critical
 * but kept for API compatibility.
 */
export function usePrefetchLibrary() {
  // No-op since data is already local
  return () => {}
}

/**
 * Invalidate library data cache.
 * Note: With reactive Automerge docs, this is less needed
 * but kept for API compatibility.
 */
export function useInvalidateLibrary() {
  // No-op since data reactively updates via useAutomergeAdapter
  return () => {}
}
